import { type KeyboardEvent, type PointerEvent, useCallback, useRef, useState } from 'react';
import { useSubjectLanguageVariants } from '@features/user';
import {
  DragIndicator as DragHandleIcon,
  PeopleAlt as PeopleIcon,
  Videocam as StartCallIcon,
  Wifi as OnlineIcon,
} from '@mui/icons-material';
import { Badge, Box, ButtonBase, Slide, Stack, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { useSnackbar } from 'notistack';

import { selectWaitingRoomLauncherYPercent, setWaitingRoomLauncherYPercent } from '../../store';
import type {
  ProviderWaitingRoomRemoveRequest,
  ProviderWaitingRoomRuntime,
  WaitingRoomEntry,
  WaitingRoomProviderAvailability,
} from '../types';
import {
  getWaitingRoomEntrySublabel,
  getWaitingRoomRemoveErrorMessage,
  isAdmittableEntry,
  isInCallWaitingRoomEntry,
  isWaitingRoomListEntry,
} from '../utils';

import { WaitingRoomEntryActionButtons } from './WaitingRoomEntryActionButtons';
import { WaitingRoomPeoplePanel } from './WaitingRoomPeoplePanel';
import { WaitingRoomRemoveDialog } from './WaitingRoomRemoveDialog';

const getPrimaryActionLabel = (entry: WaitingRoomEntry) => {
  if (isAdmittableEntry(entry)) {
    return 'Go to encounter';
  }
  return getWaitingRoomEntrySublabel(entry) ?? 'Already admitted';
};

const MIN_LAUNCHER_Y_PERCENT = 14;
const MAX_LAUNCHER_Y_PERCENT = 86;
const LAUNCHER_KEYBOARD_STEP_PERCENT = 6;

const clampLauncherYPercent = (value: number) =>
  Math.min(MAX_LAUNCHER_Y_PERCENT, Math.max(MIN_LAUNCHER_Y_PERCENT, value));

const getLauncherYPercentFromClientY = (clientY: number) => {
  if (typeof window === 'undefined' || window.innerHeight <= 0) {
    return 50;
  }

  return clampLauncherYPercent((clientY / window.innerHeight) * 100);
};

interface WaitingRoomFloatingPanelProps {
  waitingRoom: ProviderWaitingRoomRuntime;
  onStartVideoCall?: (entry: WaitingRoomEntry) => void;
}

export function WaitingRoomFloatingPanel({
  waitingRoom,
  onStartVideoCall,
}: WaitingRoomFloatingPanelProps) {
  const { enqueueSnackbar } = useSnackbar();
  const subjectLanguage = useSubjectLanguageVariants();
  const dispatch = useAppDispatch();
  const persistedLauncherYPercent = useAppSelector(selectWaitingRoomLauncherYPercent);
  const launcherRef = useRef<HTMLDivElement | null>(null);
  const dragPointerOffsetYRef = useRef(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isCopyingLink, setIsCopyingLink] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [entryPendingRemoval, setEntryPendingRemoval] = useState<WaitingRoomEntry | null>(null);
  const [dragLauncherYPercent, setDragLauncherYPercent] = useState<number | null>(null);
  const [isDraggingLauncher, setIsDraggingLauncher] = useState(false);

  const waitingEntries = waitingRoom.entries.filter(isWaitingRoomListEntry);
  const inCallParticipants = waitingRoom.entries.filter(isInCallWaitingRoomEntry).map(entry => ({
    id: entry.waiting_room_queue_entry_id,
    name: entry.patient_name,
    subtitle: 'In call',
    queueEntryId: entry.waiting_room_queue_entry_id,
  }));
  const waitingCount = waitingEntries.length;
  const hasWaitingEntries = waitingCount > 0;
  const isLauncherIdle = !hasWaitingEntries;
  const launcherYPercent = dragLauncherYPercent ?? persistedLauncherYPercent;
  const defaultExpandedEntryId =
    waitingEntries.find(isAdmittableEntry)?.waiting_room_queue_entry_id ??
    waitingEntries[0]?.waiting_room_queue_entry_id;
  const openLauncherLabel = hasWaitingEntries
    ? `Open waiting room, ${waitingCount} waiting`
    : `Open waiting room, no ${subjectLanguage.plural} waiting`;

  const handleCopyLink = useCallback(async () => {
    const shortUrl = waitingRoom.link?.short_url;
    if (!shortUrl) {
      enqueueSnackbar('Waiting room link is not available yet.', { variant: 'warning' });
      return;
    }

    setIsCopyingLink(true);
    try {
      await navigator.clipboard.writeText(shortUrl);
      setLinkCopied(true);
      enqueueSnackbar('Waiting room link copied.', { variant: 'success' });
      window.setTimeout(() => setLinkCopied(false), 1500);
    } catch {
      enqueueSnackbar('Could not copy waiting room link.', { variant: 'error' });
    } finally {
      setIsCopyingLink(false);
    }
  }, [enqueueSnackbar, waitingRoom.link?.short_url]);

  const handleSetAvailability = useCallback(
    async (availability: WaitingRoomProviderAvailability) => {
      try {
        await waitingRoom.setAvailability(availability);
      } catch {
        enqueueSnackbar('Could not update waiting room availability.', { variant: 'error' });
      }
    },
    [enqueueSnackbar, waitingRoom],
  );

  const requestRemoveFromWaitingRoom = useCallback((entry: WaitingRoomEntry) => {
    setEntryPendingRemoval(entry);
  }, []);

  const handleConfirmRemoveFromWaitingRoom = useCallback(
    async (request: ProviderWaitingRoomRemoveRequest) => {
      if (!entryPendingRemoval) {
        return;
      }

      try {
        await waitingRoom.removeQueueEntry(
          entryPendingRemoval.waiting_room_queue_entry_id,
          request,
        );
        enqueueSnackbar(`${entryPendingRemoval.patient_name} was removed from the waiting room.`, {
          variant: 'success',
        });
        setEntryPendingRemoval(null);
      } catch (error) {
        await waitingRoom.refreshQueue().catch(() => undefined);
        enqueueSnackbar(getWaitingRoomRemoveErrorMessage(error, subjectLanguage), {
          variant: 'error',
        });
      }
    },
    [enqueueSnackbar, entryPendingRemoval, subjectLanguage, waitingRoom],
  );

  const persistLauncherYPercent = useCallback(
    (value: number) => {
      dispatch(setWaitingRoomLauncherYPercent(clampLauncherYPercent(value)));
    },
    [dispatch],
  );

  const handleLauncherDragPointerDown = useCallback((event: PointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);

    const launcherRect = launcherRef.current?.getBoundingClientRect();
    if (launcherRect) {
      const launcherCenterY = launcherRect.top + launcherRect.height / 2;
      dragPointerOffsetYRef.current = event.clientY - launcherCenterY;
    } else {
      dragPointerOffsetYRef.current = 0;
    }

    setIsDraggingLauncher(true);
  }, []);

  const handleLauncherDragPointerMove = useCallback(
    (event: PointerEvent<HTMLButtonElement>) => {
      if (!isDraggingLauncher) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      setDragLauncherYPercent(
        getLauncherYPercentFromClientY(event.clientY - dragPointerOffsetYRef.current),
      );
    },
    [isDraggingLauncher],
  );

  const handleLauncherDragPointerUp = useCallback(
    (event: PointerEvent<HTMLButtonElement>) => {
      if (!isDraggingLauncher) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }

      const nextYPercent = getLauncherYPercentFromClientY(
        event.clientY - dragPointerOffsetYRef.current,
      );
      setIsDraggingLauncher(false);
      setDragLauncherYPercent(null);
      dragPointerOffsetYRef.current = 0;
      persistLauncherYPercent(nextYPercent);
    },
    [isDraggingLauncher, persistLauncherYPercent],
  );

  const handleLauncherDragPointerCancel = useCallback(
    (event: PointerEvent<HTMLButtonElement>) => {
      if (!isDraggingLauncher) {
        return;
      }

      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }

      setIsDraggingLauncher(false);
      setDragLauncherYPercent(null);
      dragPointerOffsetYRef.current = 0;
      persistLauncherYPercent(dragLauncherYPercent ?? persistedLauncherYPercent);
    },
    [dragLauncherYPercent, isDraggingLauncher, persistedLauncherYPercent, persistLauncherYPercent],
  );

  const handleLauncherDragKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>) => {
      const step = event.shiftKey
        ? LAUNCHER_KEYBOARD_STEP_PERCENT * 2
        : LAUNCHER_KEYBOARD_STEP_PERCENT;

      let nextYPercent: number | undefined;
      if (event.key === 'ArrowUp') {
        nextYPercent = launcherYPercent - step;
      } else if (event.key === 'ArrowDown') {
        nextYPercent = launcherYPercent + step;
      } else if (event.key === 'Home') {
        nextYPercent = MIN_LAUNCHER_Y_PERCENT;
      } else if (event.key === 'End') {
        nextYPercent = MAX_LAUNCHER_Y_PERCENT;
      }

      if (nextYPercent === undefined) {
        return;
      }

      event.preventDefault();
      persistLauncherYPercent(nextYPercent);
    },
    [launcherYPercent, persistLauncherYPercent],
  );

  if (!waitingRoom.hasAccess) {
    return null;
  }

  return (
    <>
      <Slide direction="left" in={isOpen} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: 'fixed',
            top: 24,
            right: 24,
            bottom: 24,
            zIndex: 1298,
            width: 'min(380px, calc(100vw - 48px))',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            overflow: 'hidden',
          }}
        >
          <WaitingRoomPeoplePanel
            entries={waitingEntries}
            providerAvailability={waitingRoom.providerAvailability}
            sseConnectionState={waitingRoom.sseConnectionState}
            link={waitingRoom.link}
            error={waitingRoom.error}
            isSettingAvailability={waitingRoom.isSettingAvailability}
            isCopyingLink={isCopyingLink}
            linkCopied={linkCopied}
            emptyLabel={`No ${subjectLanguage.plural} waiting.`}
            defaultExpandedEntryId={defaultExpandedEntryId}
            inCallParticipants={inCallParticipants}
            onRemoveInCallParticipant={participant => {
              const entry = waitingRoom.entries.find(
                waitingRoomEntry => waitingRoomEntry.waiting_room_queue_entry_id === participant.id,
              );
              if (entry) {
                requestRemoveFromWaitingRoom(entry);
              }
            }}
            entryExpandedContent={entry => (
              <WaitingRoomEntryActionButtons
                primaryLabel={getPrimaryActionLabel(entry)}
                primaryIcon={<StartCallIcon fontSize="small" />}
                onPrimaryAction={
                  onStartVideoCall && !waitingRoom.isAdmitting && isAdmittableEntry(entry)
                    ? () => {
                        setIsOpen(false);
                        onStartVideoCall(entry);
                      }
                    : undefined
                }
                onRemoveFromWaitingRoom={
                  waitingRoom.isRemoving ? undefined : () => requestRemoveFromWaitingRoom(entry)
                }
              />
            )}
            onSetAvailability={handleSetAvailability}
            onCopyLink={handleCopyLink}
            onClose={() => setIsOpen(false)}
          />
        </Box>
      </Slide>

      {!isOpen && (
        <Box
          ref={launcherRef}
          className="waiting-room-launcher"
          sx={{
            position: 'fixed',
            right: 0,
            top: `${clampLauncherYPercent(launcherYPercent)}%`,
            transform: 'translateY(-50%)',
            zIndex: 1298,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: isLauncherIdle ? 0 : 0.5,
            userSelect: isDraggingLauncher ? 'none' : undefined,
            transition: isDraggingLauncher ? 'none' : 'top 160ms ease, gap 160ms ease',
            '&:hover, &:focus-within': isLauncherIdle
              ? {
                  gap: 0.5,
                }
              : undefined,
          }}
        >
          <ButtonBase
            aria-label={openLauncherLabel}
            onClick={() => setIsOpen(true)}
            sx={{
              width: isLauncherIdle ? 38 : 56,
              minHeight: isLauncherIdle ? 92 : 176,
              borderRadius: '12px 0 0 12px',
              bgcolor: 'background.paper',
              boxShadow: isLauncherIdle
                ? '0 3px 10px rgba(0, 0, 0, 0.12)'
                : '0 8px 32px rgba(0, 0, 0, 0.2)',
              border: '1px solid',
              borderRight: 0,
              borderColor: hasWaitingEntries ? 'warning.main' : 'divider',
              px: 1,
              py: 1.5,
              textAlign: 'center',
              transform: isLauncherIdle ? 'translateX(14px)' : 'translateX(0)',
              overflow: 'hidden',
              transition:
                'width 180ms ease, min-height 180ms ease, transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
              '.waiting-room-launcher:hover &, .waiting-room-launcher:focus-within &':
                isLauncherIdle
                  ? {
                      width: 56,
                      minHeight: 176,
                      transform: 'translateX(0)',
                      borderColor: 'primary.main',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.22)',
                    }
                  : undefined,
              '&:hover': {
                width: isLauncherIdle ? 56 : 60,
                bgcolor: 'background.paper',
                borderColor: hasWaitingEntries ? 'warning.dark' : 'primary.main',
                boxShadow: isLauncherIdle
                  ? '0 8px 24px rgba(0, 0, 0, 0.22)'
                  : '0 10px 36px rgba(0, 0, 0, 0.28)',
                transform: 'translateX(0)',
              },
            }}
          >
            <Stack spacing={1} sx={{ alignItems: 'center', justifyContent: 'center' }}>
              <Badge
                badgeContent={waitingCount}
                invisible={!hasWaitingEntries}
                sx={{
                  '& .MuiBadge-badge': {
                    bgcolor: 'warning.main',
                    color: 'warning.contrastText',
                    fontWeight: 800,
                  },
                }}
              >
                <PeopleIcon color={hasWaitingEntries ? 'warning' : 'action'} />
              </Badge>
              <Box
                sx={{
                  position: 'relative',
                  width: 20,
                  height: 108,
                  flexShrink: 0,
                  ...(isLauncherIdle && {
                    height: 0,
                    opacity: 0,
                    overflow: 'hidden',
                    transition: 'height 180ms ease, opacity 140ms ease',
                    '.waiting-room-launcher:hover &, .waiting-room-launcher:focus-within &': {
                      height: 108,
                      opacity: 1,
                    },
                  }),
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    fontWeight: 800,
                    lineHeight: 1,
                    whiteSpace: 'nowrap',
                    transform: 'translate(-50%, -50%) rotate(-90deg)',
                  }}
                >
                  Waiting room
                </Typography>
              </Box>
              <Stack
                spacing={0.5}
                sx={{
                  alignItems: 'center',
                  ...(isLauncherIdle && {
                    maxHeight: 0,
                    opacity: 0,
                    overflow: 'hidden',
                    transition: 'max-height 180ms ease, opacity 140ms ease',
                    '.waiting-room-launcher:hover &, .waiting-room-launcher:focus-within &': {
                      maxHeight: 36,
                      opacity: 1,
                    },
                  }),
                }}
              >
                <OnlineIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor:
                      waitingRoom.providerAvailability === 'online'
                        ? 'success.main'
                        : 'text.disabled',
                  }}
                />
              </Stack>
            </Stack>
          </ButtonBase>
          <ButtonBase
            aria-label="Move waiting room tab"
            title="Drag to move waiting room tab"
            onPointerDown={handleLauncherDragPointerDown}
            onPointerMove={handleLauncherDragPointerMove}
            onPointerUp={handleLauncherDragPointerUp}
            onPointerCancel={handleLauncherDragPointerCancel}
            onKeyDown={handleLauncherDragKeyDown}
            sx={{
              width: isLauncherIdle ? 0 : 44,
              height: isLauncherIdle ? 0 : 18,
              opacity: isLauncherIdle ? 0 : 1,
              borderRadius: '10px 0 0 10px',
              bgcolor: 'background.paper',
              border: '1px solid',
              borderWidth: isLauncherIdle ? 0 : 1,
              borderRight: 0,
              borderColor: hasWaitingEntries ? 'warning.main' : 'divider',
              boxShadow: isLauncherIdle ? 'none' : '0 5px 20px rgba(0, 0, 0, 0.14)',
              cursor: isDraggingLauncher ? 'grabbing' : 'grab',
              pointerEvents: isLauncherIdle ? 'none' : undefined,
              touchAction: 'none',
              transform: isLauncherIdle ? 'translateX(14px)' : 'translateX(0)',
              overflow: 'hidden',
              transition:
                'width 180ms ease, height 160ms ease, opacity 120ms ease, transform 160ms ease, border-color 160ms ease, box-shadow 160ms ease',
              '.waiting-room-launcher:hover &, .waiting-room-launcher:focus-within &':
                isLauncherIdle
                  ? {
                      width: 44,
                      height: 18,
                      opacity: 1,
                      pointerEvents: 'auto',
                      borderWidth: 1,
                      transform: 'translateX(0)',
                      borderColor: 'primary.main',
                      boxShadow: '0 5px 16px rgba(0, 0, 0, 0.14)',
                    }
                  : undefined,
              '&:hover': {
                width: isLauncherIdle ? 44 : 48,
                bgcolor: 'background.paper',
                borderColor: hasWaitingEntries ? 'warning.dark' : 'primary.main',
                boxShadow: '0 7px 24px rgba(0, 0, 0, 0.18)',
                transform: 'translateX(0)',
              },
            }}
          >
            <DragHandleIcon
              sx={{ fontSize: 16, color: 'text.secondary', transform: 'rotate(90deg)' }}
            />
          </ButtonBase>
        </Box>
      )}

      <WaitingRoomRemoveDialog
        open={Boolean(entryPendingRemoval)}
        entry={entryPendingRemoval}
        isSubmitting={waitingRoom.isRemoving}
        onClose={() => setEntryPendingRemoval(null)}
        onConfirm={handleConfirmRemoveFromWaitingRoom}
      />
    </>
  );
}
